/**
 * Performance Monitoring - Lightweight Implementation for Core Kernel
 *
 * Uses the PerformanceObserver API to collect real Web Vitals
 * (LCP, FID, CLS, navigation, paint) and logs them.
 * A full monitoring SDK can be enabled via plugins for production use.
 */

export interface WebVitalsMetric {
  name: string;
  value: number;
  delta: number;
  id: string;
  rating: 'good' | 'needs-improvement' | 'poor';
}

// ── Module-level state ────────────────────────────────────

let _onReport: ((metric: WebVitalsMetric) => void) | undefined;
const _observers: PerformanceObserver[] = [];
let _metricIdCounter = 0;

// ── Rating thresholds (based on web.dev guidance) ─────────

function rateMetric(name: string, value: number): WebVitalsMetric['rating'] {
  switch (name) {
    case 'LCP':
      return value <= 2500 ? 'good' : value <= 4000 ? 'needs-improvement' : 'poor';
    case 'FID':
      return value <= 100 ? 'good' : value <= 300 ? 'needs-improvement' : 'poor';
    case 'CLS':
      return value <= 0.1 ? 'good' : value <= 0.25 ? 'needs-improvement' : 'poor';
    case 'FCP':
      return value <= 1800 ? 'good' : value <= 3000 ? 'needs-improvement' : 'poor';
    case 'TTFB':
      return value <= 800 ? 'good' : value <= 1800 ? 'needs-improvement' : 'poor';
    default:
      return 'good';
  }
}

function emitMetric(name: string, value: number, delta?: number): void {
  const metric: WebVitalsMetric = {
    name,
    value,
    delta: delta ?? value,
    id: `v${++_metricIdCounter}-${Date.now()}`,
    rating: rateMetric(name, value),
  };

  console.debug(`[Performance] ${name}: ${value.toFixed(2)} (${metric.rating})`);
  _onReport?.(metric);
}

// ── Observer setup ────────────────────────────────────────

function observeEntries(
  type: string,
  callback: (entries: PerformanceEntryList) => void,
  options?: { buffered?: boolean },
): void {
  if (typeof PerformanceObserver === 'undefined') return;

  try {
    const observer = new PerformanceObserver((list) => callback(list.getEntries()));
    observer.observe({ type, buffered: options?.buffered ?? true } as PerformanceObserverInit);
    _observers.push(observer);
  } catch {
    // Entry type not supported in this browser
  }
}

// ── Public API ────────────────────────────────────────────

/**
 * Report Web Vitals using real PerformanceObserver APIs.
 * Observes LCP, FID, CLS, navigation timing, and paint entries.
 */
export function reportWebVitals(onReport?: (metric: WebVitalsMetric) => void): void {
  _onReport = onReport;

  if (typeof PerformanceObserver === 'undefined') {
    console.log('[Performance] PerformanceObserver not available in this environment');
    return;
  }

  // Largest Contentful Paint (LCP)
  observeEntries('largest-contentful-paint', (entries) => {
    const last = entries[entries.length - 1];
    if (last) emitMetric('LCP', last.startTime);
  });

  // First Input Delay (FID)
  observeEntries('first-input', (entries) => {
    const first = entries[0] as PerformanceEventTiming;
    if (first) emitMetric('FID', first.processingStart - first.startTime);
  });

  // Cumulative Layout Shift (CLS)
  let clsValue = 0;
  observeEntries('layout-shift', (entries) => {
    for (const entry of entries) {
      if (!(entry as any).hadRecentInput) {
        clsValue += (entry as any).value ?? 0;
      }
    }
    emitMetric('CLS', clsValue);
  });

  // Navigation timing (TTFB)
  observeEntries('navigation', (entries) => {
    const nav = entries[0] as PerformanceNavigationTiming;
    if (nav) {
      emitMetric('TTFB', nav.responseStart - nav.requestStart);
    }
  });

  // First Contentful Paint (FCP)
  observeEntries('paint', (entries) => {
    for (const entry of entries) {
      if (entry.name === 'first-contentful-paint') {
        emitMetric('FCP', entry.startTime);
      }
    }
  });

  console.log('[Performance] Web Vitals observers registered');
}

/**
 * Initialize performance monitoring — starts observers and logs metrics.
 */
export function initPerformanceMonitoring(): void {
  console.log('[Performance] Performance monitoring initialized');
  reportWebVitals();
}

/**
 * Mark the start of a performance measurement.
 */
export function measureStart(name: string): void {
  if (typeof performance !== 'undefined') {
    performance.mark(`${name}-start`);
  }
}

/**
 * End a performance measurement and return the duration.
 */
export function measureEnd(name: string): number | null {
  if (typeof performance !== 'undefined') {
    try {
      performance.mark(`${name}-end`);
      const measure = performance.measure(name, `${name}-start`, `${name}-end`);
      return measure.duration;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Get current performance entries.
 */
export function getPerformanceEntries(): PerformanceEntry[] {
  if (typeof performance !== 'undefined') {
    return performance.getEntries();
  }
  return [];
}

/**
 * Clear performance marks and disconnect observers.
 */
export function clearPerformanceMarks(): void {
  if (typeof performance !== 'undefined') {
    performance.clearMarks();
    performance.clearMeasures();
  }
}
